import PauseIcon from "@mui/icons-material/Pause";
import SaveIcon from "@mui/icons-material/Save";
import {
  alpha,
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef } from "react";
import { HistoryItemWrapper } from "../components/history-item-wrapper";
import { useWebSocketContext } from "../contexts/websocket-context";
import { useApiMutation, useApiQuery } from "../hooks";
import { useAutoScroll } from "../hooks/use-auto-scroll";
import { theme } from "../theme/theme";
import type { AiRequest, AiResponse, AppConfig, Test } from "../types";
import type {
  TestAgentV2BlockEvent,
  TestAgentV2ConfigEvent,
  TestAgentV2Event,
  TestAgentV2HistoryEvent,
  TestAgentV2IncomingEvent,
  TestAgentV2StatusEvent,
  TestAgentV2TestEvent,
} from "../types/test-agent-2";

export const GenerateFastChat = () => {
  const params = new URLSearchParams(window.location.search);
  const debug = params.get("debug") === "true";
  const initialText = params.get("text") || "";
  const depIdFromQuery = params.get("depId");

  const [testName, setTestName] = React.useState<string>("");
  const [message, setMessage] = React.useState<string>(initialText);
  const [hasSetDependency, setHasSetDependency] = React.useState(false);

  const { send, messages } = useWebSocketContext<TestAgentV2IncomingEvent, TestAgentV2Event>();

  const {
    mutate: ai,
    data: aiData,
    isPending: isAiProcessing,
    reset: resetAi,
  } = useApiMutation<AiRequest, AiResponse<{ arrange: string; act: string; assert: string }[]>>(
    "POST",
    "/ai/response",
  );

  const history = useMemo(() => {
    return messages
      .filter((msg) => msg.type === "history")
      .reduce((acc, msg) => {
        // Reduce by `id` to avoid duplicates and return `data`
        const historyMsg = msg.data as TestAgentV2HistoryEvent;
        if (!acc.find((m) => m.history.id === historyMsg.history.id)) {
          acc.push(historyMsg);
        } else {
          acc = acc.map((m) => (m.history.id === historyMsg.history.id ? historyMsg : m));
        }
        return acc;
      }, [] as TestAgentV2HistoryEvent[]);
  }, [messages]);

  const blocks = useMemo(() => {
    return messages
      .filter((msg) => msg.type === "block")
      .map((msg) => (msg.data as TestAgentV2BlockEvent).block)
      .sort((a, b) => a.id - b.id);
  }, [messages]);

  const lastStatus = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].type === "status") {
        return (messages[i].data as TestAgentV2StatusEvent).status;
      }
    }

    return "pending";
  }, [messages]);

  const tasksPending = useMemo(() => {
    if (lastStatus === "pending" && history.length > 0) {
      return true;
    }

    if (history.length === 0) {
      return false;
    }

    const lastHistory = history[history.length - 1];
    return lastHistory.history.intents.some((i) => i.intent.tasks_pending);
  }, [history, lastStatus]);

  const test = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].type === "test") {
        return messages[i].data as TestAgentV2TestEvent;
      }
    }
  }, [messages]);

  useEffect(() => {
    if (test) {
      setTestName(test.test.name);
    }
  }, [test]);

  const configEvent = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].type === "config") {
        return messages[i].data as TestAgentV2ConfigEvent;
      }
    }
  }, [messages]);

  const testDepId = useMemo(() => {
    const deps = configEvent?.test_dependency_ids || [];

    if (deps.length === 0) {
      return null;
    }

    return deps[0];
  }, [configEvent]);

  // Auto-set dependency from query param
  useEffect(() => {
    if (depIdFromQuery && !hasSetDependency && configEvent && testDepId !== depIdFromQuery) {
      send({ type: "set_test_dependency", id: depIdFromQuery });
      setHasSetDependency(true);
    }
  }, [depIdFromQuery, hasSetDependency, configEvent, testDepId, send]);

  const { data: config } = useApiQuery<AppConfig>(`/config/${configEvent?.app_config_id}`, {
    enabled: !!configEvent?.app_config_id,
    refetchInterval: undefined,
  });

  const { data: testDep, refetch } = useApiQuery<Test>(`/tests/${testDepId}`, {
    enabled: !!testDepId,
    refetchInterval: undefined,
  });

  useEffect(() => {
    if (testDepId) {
      refetch();
    }
  }, [testDepId, refetch]);

  useEffect(() => {
    if (history.length > 0) {
      resetAi();
      return;
    }

    if (!config) {
      return;
    }

    let query = `Propose a 3 QA tasks for the test based on the following information:\n\n`;

    if (config) {
      query += `- Application name: ${config.name}\n`;
      query += `- Application base URL: ${config.base_url}\n`;
      query += `- Application login URL: ${config.login_url}\n`;
    }

    if (testDep) {
      query += `\n- The QA tasks start assuming the dependency "${testDep.name}" has been completed (seeded).`;
    } else {
      query += `\n- The QA tasks start from the beginning, no dependencies.`;
    }

    query += `\n\n<remarks>\nEach task should be self-descriptive. Propose up to 3 tasks. Do not ask questions. Use the AAA (Arrange, Act, Assert) pattern to describe each task, be specific. Use markdown with ordered list. Do not include any other text, nor reference directly the AAA pattern. Each task should be a standalone AAA task.\n</remarks>`;

    query += `\nHere's an example of a good QA task:\n---\n1. Navigate to the login page at ${config.login_url}.\n2. Enter valid user credentials (username and password) into the respective fields.\n3. Click the "Login" button to submit the form.\n4. Verify that the user is redirected to the dashboard page and that a welcome message is displayed.\n---\nThis is an example of a SINGLE task, you will need to propose up to 3 of these, make sure to make them disjoint and not overlapping so that they can be executed independently, and are meaningful on their own.\n\n`;

    ai({
      query,
      use_app_context: true,
      output_model: {
        type: "array",
        items: {
          type: "object",
          description: "A QA task with arrange, act, assert steps",
          properties: {
            arrange: {
              type: "string",
              description: "The arrange step of the task.",
            },
            act: {
              type: "string",
              description: "The act step of the task.",
            },
            assert: {
              type: "string",
              description: "The assert step of the task.",
            },
          },
        },
        name: "next_qa_tasks",
        description: "An array of up to 3 QA tasks",
      },
    });
  }, [config?.name, testDep, history?.length, ai, resetAi, config]);

  const yoloMode = useMemo(() => {
    return configEvent?.autopilot || false;
  }, [configEvent]);

  const messagesScrollRef = useRef<HTMLDivElement>(null);
  useAutoScroll(messagesScrollRef.current, history.length);

  const aiResponses = useMemo(() => {
    if (!aiData?.resp || !Array.isArray(aiData.resp)) {
      return [];
    }

    return aiData.resp;
  }, [aiData]);

  return (
    <Stack
      data-testid="generate-fast-chat"
      direction="column"
      spacing={2}
      alignItems="stretch"
      justifyContent="stretch"
      sx={{
        py: 2,
        height: "calc(100vh - 64px - 32px)",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
        <TextField
          variant="outlined"
          size="small"
          label={test?.test.name === testName ? "Name" : "Name (unsaved, press Enter to save)"}
          placeholder="e.g. 'User login journey'"
          value={testName}
          onChange={(e) => {
            setTestName(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              send({
                type: "change_name",
                message: testName.trim() || "New Chat",
              });
            }
          }}
          autoFocus
          sx={{ flex: 1, maxWidth: "600px" }}
        />
        <Tooltip title="Persist the test">
          <IconButton
            aria-label="Save test"
            onClick={() => {
              send({
                type: "save_test",
                message: testName.trim() ?? test?.test.name ?? "New Test",
              });
            }}
            size="small"
            color="primary"
          >
            <SaveIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
        }}
        ref={messagesScrollRef}
      >
        <Stack
          direction="column"
          alignItems="stretch"
          justifyContent="stretch"
          spacing={2}
          divider={<Divider />}
          height="100%"
        >
          {history.length === 0 && (
            <Stack
              spacing={2}
              height="100%"
              alignItems="center"
              justifyContent="flex-end"
              divider={
                <Divider orientation="horizontal" flexItem>
                  <Typography variant="caption" color="text.secondary">
                    or start with one of these
                  </Typography>
                </Divider>
              }
            >
              <Typography variant="body1" textAlign="center" maxWidth="400px">
                Type what you want the test to do in the input below, and let the agent handle the
                rest!
              </Typography>
              {isAiProcessing && (
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                  <CircularProgress size={14} color="inherit" />
                  <Typography variant="body2">Thinking some suggestions...</Typography>
                </Stack>
              )}
              {!isAiProcessing && aiResponses.length > 0 && (
                <Stack direction="column" spacing={1} alignItems="stretch" width="100%">
                  {aiResponses.map((resp, index) => {
                    const text = `1. ${resp.arrange}\n2. ${resp.act}\n3. ${resp.assert}`;

                    return (
                      <Button
                        key={index}
                        variant="outlined"
                        size="small"
                        color="primary"
                        onClick={() => {
                          setMessage(text);
                        }}
                        fullWidth
                        sx={{
                          minHeight: "100px",
                          width: "100%",
                          color: theme.palette.text.primary,
                          backgroundColor: alpha(theme.palette.background.paper, 0.25),
                          borderColor: alpha(theme.palette.primary.main, 0.2),
                          "&:hover": {
                            color: theme.palette.primary.main,
                            borderColor: theme.palette.primary.main,
                          },
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            whiteSpace: "pre-wrap",
                            textAlign: "start",
                            width: "100%",
                          }}
                        >
                          {text}
                        </Typography>
                      </Button>
                    );
                  })}
                </Stack>
              )}
            </Stack>
          )}
          {history.map(({ history: historyItem }, index) => (
            <HistoryItemWrapper
              key={index}
              history={historyItem}
              blocks={blocks}
              debug={debug}
              isLast={index === history.length - 1}
            />
          ))}
        </Stack>
      </Box>
      <Card
        sx={{
          flexShrink: 0,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          backgroundColor: "common.white",
          "&:focus-within": {
            borderColor: "info.dark",
            boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.12)",
          },
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          sx={{
            p: 1.5,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.primary.main, 1)}`,
          }}
        >
          <TextField
            variant="outlined"
            size="small"
            multiline
            minRows={1}
            maxRows={4}
            placeholder="What is the next task? (Shift + ↵ for newline)"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => {
              // Enter is submit, shift+enter is newline
              if (e.key === "Enter") {
                if (e.shiftKey) {
                  return;
                }

                if (message.trim() !== "") {
                  e.preventDefault();

                  send({
                    type: "next_task",
                    message: message.trim(),
                  });
                  setMessage("");
                } else if (tasksPending || lastStatus === "paused") {
                  e.preventDefault();

                  send({
                    type: "next_task",
                    message: "Continue",
                  });
                }
              }
            }}
            fullWidth
            InputProps={{
              disableUnderline: true,
              sx: {
                borderRadius: 3,
                "& fieldset": {
                  border: "none",
                },
                fontSize: 16,
                color: theme.palette.text.primary,
                backgroundColor: alpha(theme.palette.background.paper, 0.9),
                alignItems: "center",
                "& .MuiInputBase-inputMultiline": {
                  display: "flex",
                  alignItems: "center",
                  minHeight: "24px",
                },
                "& textarea::placeholder": {
                  color: alpha(theme.palette.text.secondary, 0.6),
                  opacity: 1,
                },
              },
            }}
          />
          {(lastStatus === "running" ||
            ((tasksPending || lastStatus === "paused") && !message) ||
            (tasksPending && !yoloMode && !message)) && (
            <Stack direction="row" spacing={1} alignItems="center">
              {lastStatus === "running" && (
                <Tooltip title="Pause agent">
                  <span>
                    <IconButton
                      color="default"
                      size="small"
                      onClick={() => {
                        send({
                          type: "pause",
                        });
                      }}
                    >
                      <PauseIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {tasksPending && lastStatus !== "running" && (
                <Stack direction="column" spacing={1} sx={{ width: 120 }}>
                  {(tasksPending || lastStatus === "paused") && !message && (
                    <Button
                      variant="outlined"
                      size="small"
                      color="primary"
                      onClick={() =>
                        send({
                          type: "next_task",
                          message: "Continue",
                        })
                      }
                      fullWidth
                    >
                      Continue (↵)
                    </Button>
                  )}
                  {tasksPending && !message && !yoloMode && (
                    <Button
                      variant="contained"
                      size="small"
                      color="warning"
                      onClick={() =>
                        send({
                          type: "toggle_autopilot",
                          message: "Continue",
                        })
                      }
                      fullWidth
                    >
                      YOLO
                    </Button>
                  )}
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
      </Card>
    </Stack>
  );
};
